import React, { createContext, useContext, useState, useEffect } from 'react';
import { getPlayers } from '../firebase';
import { useAuth } from './AuthContext';

const SelectedPlayersContext = createContext();

export const useSelectedPlayers = () => {
  const context = useContext(SelectedPlayersContext);
  if (!context) {
    throw new Error(
      'useSelectedPlayers must be used within a SelectedPlayersProvider'
    );
  }
  return context;
};

export const SelectedPlayersProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
  const [selectedPlayersData, setSelectedPlayersData] = useState([]);
  const [allPlayersData, setAllPlayersData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load all players data when user changes
  useEffect(() => {
    const loadAllPlayersData = async () => {
      if (authLoading) {
        return;
      }

      if (!user) {
        return;
      }

      setLoading(true);
      try {
        const allPlayers = await getPlayers(user.uid);
        setAllPlayersData(allPlayers);
      } catch (error) {
        console.error('Error loading all players data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAllPlayersData();
  }, [user, authLoading]);

  // Immediate load if user is already available
  useEffect(() => {
    if (!authLoading && user) {
      const loadData = async () => {
        setLoading(true);
        try {
          const allPlayers = await getPlayers(user.uid);
          setAllPlayersData(allPlayers);
        } catch (error) {
          console.error('Error loading players data:', error);
        } finally {
          setLoading(false);
        }
      };
      loadData();
    }
  }, [authLoading, user]);

  // Clean up any invalid localStorage data on mount
  useEffect(() => {
    const savedSelectedPlayers = localStorage.getItem('selectedPlayerIds');
    if (
      savedSelectedPlayers === 'undefined' ||
      savedSelectedPlayers === 'null'
    ) {
      localStorage.removeItem('selectedPlayerIds');
    }
  }, []);

  // Load selected players from localStorage on mount (only once)
  useEffect(() => {
    const savedSelectedPlayers = localStorage.getItem('selectedPlayerIds');
    if (savedSelectedPlayers && savedSelectedPlayers !== 'undefined') {
      try {
        const parsed = JSON.parse(savedSelectedPlayers);
        setSelectedPlayerIds(parsed);
      } catch (error) {
        console.error(
          'Error parsing selected players from localStorage:',
          error
        );
        // Clear the invalid localStorage data
        localStorage.removeItem('selectedPlayerIds');
      }
    }
  }, []);

  // Combined effect: Update selected players data and clean up stale IDs
  useEffect(() => {
    if (allPlayersData.length === 0) return;

    // Clean up stale player IDs
    if (selectedPlayerIds.length > 0) {
      const validPlayerIds = allPlayersData.map((player) => player.id);
      const validSelectedIds = selectedPlayerIds.filter((id) =>
        validPlayerIds.includes(id)
      );

      // If we filtered out any invalid IDs, update the selection
      if (validSelectedIds.length !== selectedPlayerIds.length) {
        setSelectedPlayers(validSelectedIds);
        return; // Don't update selectedPlayersData yet, wait for the next render
      }
    }

    // Update selected players data
    if (selectedPlayerIds.length === 0) {
      setSelectedPlayersData([]);
    } else {
      const selectedData = allPlayersData.filter((player) =>
        selectedPlayerIds.includes(player.id)
      );
      setSelectedPlayersData(selectedData);
    }
  }, [allPlayersData, selectedPlayerIds]);

  const setSelectedPlayers = (playerIds) => {
    setSelectedPlayerIds(playerIds);
    // Only store in localStorage if playerIds is a valid array
    if (Array.isArray(playerIds)) {
      localStorage.setItem('selectedPlayerIds', JSON.stringify(playerIds));
    } else {
      localStorage.removeItem('selectedPlayerIds');
    }
  };

  const removePlayer = (playerId) => {
    const newSelectedIds = selectedPlayerIds.filter((id) => id !== playerId);
    setSelectedPlayers(newSelectedIds);
  };

  const addPlayer = (playerId) => {
    if (!selectedPlayerIds.includes(playerId)) {
      const newSelectedIds = [...selectedPlayerIds, playerId];
      setSelectedPlayers(newSelectedIds);
    }
  };

  const clearSelectedPlayers = () => {
    setSelectedPlayers([]);
    localStorage.removeItem('selectedPlayerIds');
  };

  const forceLoadPlayers = async () => {
    if (!user) {
      return;
    }

    setLoading(true);
    try {
      const allPlayers = await getPlayers(user.uid);
      setAllPlayersData(allPlayers);
    } catch (error) {
      console.error('Error loading players:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateAllPlayersData = (playersData) => {
    setAllPlayersData(playersData);
  };

  const value = {
    selectedPlayerIds,
    selectedPlayersData,
    allPlayersData,
    setSelectedPlayers,
    removePlayer,
    addPlayer,
    clearSelectedPlayers,
    forceLoadPlayers,
    updateAllPlayersData,
    loading,
  };

  return (
    <SelectedPlayersContext.Provider value={value}>
      {children}
    </SelectedPlayersContext.Provider>
  );
};
